import { useState, useContext } from "react";
import ReactDOM from "react-dom";
import {
  FaCheckCircle,
  FaEye,
  FaHeart,
  FaShoppingCart,
  FaStore,
  FaTimes,
  FaMinus,
  FaPlus,
  FaTrashAlt,
  FaBan,
  FaExclamationCircle,
  FaTruck,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "../../../../assets/css/components/customer/cards/productCard/ProductCard.css";
import MyContext from "../../../../context/MyContext";
import Swal from "sweetalert2";
import axios from "axios";
import { PLACEHOLDER_PRODUCT } from "../../../../utils/placeholder";

const ProductCard = ({
  product,
  index,
  baseUrlForFiles,
  isWishlisted = false,
  onRemoveFromWishlist,
}) => {
  const navigate = useNavigate();
  const [isLiked, setIsLiked]           = useState(isWishlisted);
  const [inCart, setInCart]             = useState(false);
  const [showQtyModal, setShowQtyModal] = useState(false);
  const [quantity, setQuantity]         = useState(1);

  const [toast, setToast] = useState(null);

  const {
    baseUrl,
    baseUrlForFiles: ctxBaseUrlForFiles,
    loggedInCustomer,
    authToken,
    setSelectedProduct,
    cartProducts,
    setCartProducts,
  } = useContext(MyContext);

  const filesUrl = baseUrlForFiles || ctxBaseUrlForFiles;
  const minQtyRaw = product.min_order_quantity || 1;
  const minQty = Math.min(minQtyRaw, Number(product.stock));
  const presets = [minQty, minQty * 2, minQty * 5, minQty * 10].filter(
    (v, i, arr) => arr.indexOf(v) === i && v <= product.stock,
  );

  const isOutOfStock = Number(product.stock) <= 0;

  // ── Intercity Bulk Delivery — is product ka apna city se bahar
  // seller ho aur bulk_threshold_quantity set ho, to badge dikhao.
  // Ye sirf ek UX hint hai — asal check checkout ke address-step pe
  // hoti hai (CustomerCart.js), yahan koi enforcement nahi ──
  const sellerCity = product?.kmseller?.city;
  const customerCity = loggedInCustomer?.city;
  const isDifferentCity =
    customerCity && sellerCity && customerCity.toLowerCase() !== sellerCity.toLowerCase();
  const showBulkBadge = isDifferentCity && product?.bulk_threshold_quantity;

  const existingCartItem = cartProducts?.find(
    (c) => c.kmproduct_id === product.id && !c.isPurchased
  );

  const conflictingSellerItem = cartProducts?.find(
    (c) =>
      !c.isPurchased &&
      c.kmproduct_id !== product.id &&
      c.kmproduct?.kmseller?.id &&
      product?.kmseller?.id &&
      c.kmproduct.kmseller.id !== product.kmseller.id,
  );

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2500);
  };

  // ── toggleWishlist — login-gate: loggedInCustomer AUR authToken dono check ──
  const toggleWishlist = () => {
    if (!loggedInCustomer?.id || !authToken) {
      localStorage.setItem(
        "pendingAction",
        JSON.stringify({
          type: "wishlist",
          product,
          returnPath: window.location.pathname,
        }),
      );
      navigate("/customer/login");
      return;
    }

    const formData = new FormData();
    formData.append("kmproduct_id", product.id);

    axios
      .post(`${baseUrl}/customer/wishlist/toggleWishlist`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          showToast("error", response.data.error);
        } else {
          if (response.data.action === "added") {
            setIsLiked(true);
            showToast("success", "Product added to wishlist!");
          } else {
            setIsLiked(false);
            showToast("success", "Product removed from wishlist!");
          }
        }
      })
      .catch((error) => {
        console.log(error);
        if (error.response?.status === 401) {
          localStorage.setItem(
            "pendingAction",
            JSON.stringify({
              type: "wishlist",
              product,
              returnPath: window.location.pathname,
            }),
          );
          navigate("/customer/login");
          return;
        }
        showToast("error", "Failed to update wishlist");
      });
  };

  // ── openQuantityModal — login-gate: loggedInCustomer AUR authToken dono check ──
  const openQuantityModal = () => {
    if (isOutOfStock) return;

    if (!loggedInCustomer?.id || !authToken) {
      localStorage.setItem(
        "pendingAction",
        JSON.stringify({
          type: "cart",
          product,
          quantity: existingCartItem ? existingCartItem.quantity : minQty,
          returnPath: window.location.pathname,
        }),
      );
      navigate("/customer/login");
      return;
    }

    if (!existingCartItem && conflictingSellerItem) {
      const sellerName =
        conflictingSellerItem.kmproduct?.kmseller?.shopname || "another seller";
      showToast(
        "error",
        `Your cart already has a product from ${sellerName}. Please clear or checkout that cart first.`,
      );
      return;
    }

    setQuantity(existingCartItem ? existingCartItem.quantity : minQty);
    setShowQtyModal(true);
  };

  // ── addProductToCart — safety net: agar beech mein 401 aaye to login pe bhej dein ──
  const addProductToCart = () => {
    const formData = new FormData();
    formData.append("kmproduct_id",  product.id);
    formData.append("amountPerPiece", product.price);
    formData.append("quantity",       quantity);
    formData.append("total_price",    product.price * quantity);

    axios
      .post(`${baseUrl}/customer/cart/addProductToCart`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          showToast("error", response.data.error);
          return;
        }

        if (response.data.success === false) {
          if (response.data.differentSeller) {
            showToast(
              "error",
              `Your cart already has a product from ${response.data.sellerName}. Please clear or checkout that cart first.`,
            );
          } else {
            showToast("error", response.data.message);
          }
          return;
        }

        setInCart(true);
        setShowQtyModal(false);
        // Backend ab cart mein add hote hi wishlist se bhi hata deta hai —
        // heart icon ko turant sync kar dein taake reload ka wait na karna pare
        setIsLiked(false);

        const cartItemId = response.data.cartItem?.id;
        const newQuantity = quantity;

        if (existingCartItem) {
          setCartProducts((prev) =>
            prev.map((c) =>
              c.kmproduct_id === product.id
                ? { ...c, id: cartItemId ?? c.id, quantity: newQuantity, total_price: newQuantity * product.price }
                : c
            )
          );
        } else {
          setCartProducts((prev) => [
            ...prev,
            {
              id:             cartItemId,
              kmproduct_id:   product.id,
              kmproduct:      product,
              quantity:       quantity,
              amountPerPiece: product.price,
              total_price:    product.price * quantity,
              isPurchased:    false,
            },
          ]);
        }

        showToast(
          "success",
          `${product.name} ${existingCartItem ? "updated in" : "added to"} cart!`,
        );
        setQuantity(minQty);
      })
      .catch((error) => {
        console.log("cart API error:", error);
        if (error.response?.status === 401) {
          setShowQtyModal(false);
          localStorage.setItem(
            "pendingAction",
            JSON.stringify({
              type: "cart",
              product,
              quantity,
              returnPath: window.location.pathname,
            }),
          );
          navigate("/customer/login");
          return;
        }
        showToast("error", "Failed to add product to cart.");
      });
  };

  const removeFromCart = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!existingCartItem?.id) return;

    Swal.fire({
      title: "Remove from cart?",
      text: "This product will be removed from your cart.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, remove it",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const formData = new FormData();
      formData.append("id", existingCartItem.id);

      axios
        .post(`${baseUrl}/customer/cart/removeProductFromCart`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        })
        .then((response) => {
          if (response.data.hasOwnProperty("error") || response.data.success === false) {
            showToast("error", response.data.error || response.data.message);
            return;
          }
          setCartProducts((prev) => prev.filter((c) => c.id !== existingCartItem.id));
          setInCart(false);
          showToast("success", "Product removed from cart.");
        })
        .catch(() => showToast("error", "Failed to remove from cart."));
    });
  };

  return (
    <>
      <div
        className={`pcard-wrapper ${isOutOfStock ? "pcard-out-of-stock" : ""}`}
        onClick={() => {
          setSelectedProduct(product);
          navigate(`/product-detail`);
        }}
        style={{ "--animation-delay": `${index * 0.1}s` }}
      >
        <div className="pcard-image-wrapper">
          <img
            src={`${filesUrl}/${product.image}`}
            alt={product.name}
            className="pcard-image"
            style={isOutOfStock ? { filter: "grayscale(70%)", opacity: 0.7 } : {}}
          />

          {isOutOfStock && (
            <div className="pcard-out-of-stock-badge">
              <FaBan size={12} style={{ marginRight: 4 }} />
              Out of Stock
            </div>
          )}

          {product.badge && !isOutOfStock && (
            <div className={`pcard-badge pcard-badge-${product.badge.type}`}>
              {product.badge.text}
            </div>
          )}

          {/* ── Bulk-order badge (Intercity Bulk Delivery spec) ── */}
          {showBulkBadge && !isOutOfStock && (
            <div className="pcard-badge pcard-badge-bulk" title={`Minimum ${product.bulk_threshold_quantity} ${product.unit || ""} required for intercity delivery`}>
              <FaTruck size={11} style={{ marginRight: 4 }} />
              Bulk order only for delivery outside {sellerCity}
            </div>
          )}

          <div className="pcard-actions">
            <button
              className={`pcard-action-icon ${isLiked ? "pcard-active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (onRemoveFromWishlist) {
                  onRemoveFromWishlist(product.id);
                } else {
                  toggleWishlist();
                }
              }}
              title={isLiked ? "Remove from wishlist" : "Add to wishlist"}
            >
              <FaHeart />
            </button>
            <button
              className="pcard-action-icon"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setSelectedProduct(product);
                navigate(`/product-detail`);
              }}
              title="Quick view"
            >
              <FaEye />
            </button>
          </div>
        </div>

        <div className="pcard-info">
          <span className="pcard-category">{product.subcategory}</span>
          <h3 className="pcard-name">{product.name}</h3>
          {product.shopName && (
            <div className="pcard-shop">
              <FaStore />
              <span>{product.shopName}</span>
            </div>
          )}
          <div className="pcard-price">
            <span className="pcard-price-current">
              Rs. {product.price?.toLocaleString()}
              {product.unit && <span className="pcard-unit"> / {product.unit}</span>}
            </span>
            {product.oldPrice && (
              <span className="pcard-price-old">
                Rs. {product.oldPrice.toLocaleString()}
              </span>
            )}
          </div>

          <div className="pcard-cart-btn-row" style={{ display: "flex", gap: 6 }}>
            <button
              className="ppcard-cart-button"
              disabled={isOutOfStock}
              style={isOutOfStock ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                openQuantityModal();
              }}
              type="button"
            >
              {isOutOfStock ? (
                <><FaBan /><span>Out of Stock</span></>
              ) : existingCartItem ? (
                <><FaCheckCircle /><span>In Cart ({existingCartItem.quantity})</span></>
              ) : (
                <><FaShoppingCart /><span>Add to Cart</span></>
              )}
            </button>

            {existingCartItem && (
              <button
                className="pcard-remove-cart-button"
                onClick={removeFromCart}
                title="Remove from cart"
                type="button"
              >
                <FaTrashAlt size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {showQtyModal && ReactDOM.createPortal(
        <div className="pcard-qty-overlay" onClick={() => setShowQtyModal(false)}>
          <div className="pcard-qty-modal" onClick={(e) => e.stopPropagation()}>

            <div className="pcard-qty-header">
              <h4 className="pcard-qty-title">Select Quantity</h4>
              <button className="pcard-qty-close" onClick={() => setShowQtyModal(false)}>
                <FaTimes size={16} />
              </button>
            </div>

            {existingCartItem && (
              <div className="pcard-qty-in-cart-banner">
                <FaCheckCircle size={13} />
                Already in cart: <strong>{existingCartItem.quantity}</strong>
              </div>
            )}

            {/* ── Bulk-order note in modal too, taake customer address-step
                se pehle hi quantity samajh sakay ── */}
            {showBulkBadge && (
              <div className="pcard-qty-in-cart-banner" style={{ background: "#fff3cd", color: "#856404" }}>
                <FaTruck size={13} />
                Delivery outside {sellerCity} needs at least {product.bulk_threshold_quantity} {product.unit || ""}
              </div>
            )}

            <div className="pcard-qty-product">
              <img
                src={`${filesUrl}/${product.image}`}
                alt={product.name}
                className="pcard-qty-image"
                onError={(e) => {
                  e.currentTarget.onerror = null; // loop band
                  e.currentTarget.src = PLACEHOLDER_PRODUCT;
                }}
              />
              <div className="pcard-qty-info">
                <h5 className="pcard-qty-name">{product.name}</h5>
                {product.subcategory && (
                  <span className="pcard-qty-category">{product.subcategory}</span>
                )}
                <div className="pcard-qty-price">
                  Rs. {product.price?.toLocaleString()}
                  {product.unit && <span className="pcard-qty-unit"> / {product.unit}</span>}
                </div>
                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                  Min Order: {minQty} | In Stock: {product.stock}
                </div>
              </div>
            </div>

            <hr className="pcard-qty-divider" />

            <p className="pcard-qty-label">Quick Select</p>
            <div className="pcard-qty-presets">
              {presets.map((val) => (
                <button
                  key={val}
                  className={`pcard-qty-preset-btn ${quantity === val ? "active" : ""}`}
                  onClick={() => setQuantity(val)}
                  disabled={val > product.stock}
                >
                  {val}
                </button>
              ))}
            </div>

            <div className="pcard-qty-controls">
              <button
                className="pcard-qty-btn"
                onClick={() => setQuantity((q) => Math.max(minQty, q - 1))}
              >
                <FaMinus size={12} />
              </button>
              <input
                type="number"
                className="pcard-qty-input"
                value={quantity}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (!isNaN(val) && val >= 1) setQuantity(val);
                }}
                min={minQty}
              />
              <button
                className="pcard-qty-btn"
                onClick={() => setQuantity((q) => Math.min(product.stock, (q || minQty) * 2))}
              >
                <FaPlus size={12} />
              </button>
            </div>

            {quantity < minQty && (
              <p style={{ color: "#d33", fontSize: 12, marginTop: 4 }}>
                Minimum order quantity is {minQty}.
              </p>
            )}
            {quantity > product.stock && (
              <p style={{ color: "#d33", fontSize: 12, marginTop: 4 }}>
                Only {product.stock} in stock.
              </p>
            )}

            <div className="pcard-qty-total">
              <span>Total:</span>
              <span className="pcard-qty-total-value">
                Rs. {(product.price * quantity)?.toLocaleString()}
              </span>
            </div>

            <div className="pcard-qty-footer">
              <button className="pcard-qty-cancel-btn" onClick={() => setShowQtyModal(false)}>
                Cancel
              </button>
              <button
                className="pcard-qty-confirm-btn"
                onClick={addProductToCart}
                disabled={quantity < minQty || quantity > product.stock}
                style={quantity < minQty || quantity > product.stock ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              >
                <FaShoppingCart size={13} style={{ marginRight: 6 }} />
                {existingCartItem ? "Update Cart" : "Add to Cart"}
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}

      {toast && ReactDOM.createPortal(
        <div className={`pcard-toast ${toast.type === "error" ? "pcard-toast-error" : ""}`}>
          <div className="pcard-toast-content">
            {toast.type === "error" ? (
              <FaExclamationCircle className="pcard-toast-icon" />
            ) : (
              <FaCheckCircle className="pcard-toast-icon" />
            )}
            <span>{toast.message}</span>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ProductCard;
